import React, {useCallback, useEffect, useRef, useState} from 'react';
import {inject} from 'mobx-react';
import {observer} from 'mobx-react-lite';
import I18n from 'app/i18n';
import {configStore, userStore} from 'app/store/global';
import {ModalsStore} from 'app/store/ModalsStore';
import SelectPicker from 'app/components/reusable/SelectPicker';
import CollectionInterface from 'app/interfaces/CollectionInterface';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import Post from 'app/utils/Post';
import Patch from 'app/utils/Patch';
import {isDesktop} from 'app/utils';
import {UNKNOWN_COUNTRY} from 'app/utils/Consts';
import {confirmCountryChoice} from 'app/utils/countryRestrictions';
import {noticeAction, openCountryList, whenTabVisible} from 'app/utils/countryConfirmation';
import {IntersectionElement} from 'app/utils/lazyLoading/IntersectionElement';
import {askAboutCountryRestrictions} from 'app/components/auth/CountryRestrictionsDialog';

const CONFIRMATION_URL = '/country_confirmation';
const NOTHING_TO_CONFIRM = 204;

interface AnswerParams {
    result: 'confirmed' | 'changed' | 'closed'
    country_code?: string
}

interface CardProps {
    modalsStore?: ModalsStore
}

type StopWaiting = () => void;

interface ShownReport {
    handleCardOnScreen: () => void
    markShown: () => Promise<boolean>
}

interface Answering {
    answer: (params: AnswerParams) => Promise<void>
    failed: boolean
    setSubmitting: (submitting: boolean) => void
    submitting: boolean
}

const countryFlags = (): CollectionInterface[] => configStore.REG_FLAGS.map((flag): CollectionInterface => ({
    image: flag.image,
    text: flag.name,
    value: flag.code
}));

const reportShown = async(): Promise<boolean> => {
    const response = await new Post({url: CONFIRMATION_URL}).execute();

    if (response.status === NOTHING_TO_CONFIRM) {
        userStore.hideCountryConfirmation();
        return false;
    }

    return response.ok;
};

const useShownReport = (): ShownReport => {
    const shown = useRef<Promise<boolean> | null>(null);
    const stopWaiting = useRef<StopWaiting | null>(null);

    const markShown = useCallback((): Promise<boolean> => {
        shown.current ||= reportShown().catch(() => false).then(recorded => {
            if (!recorded) {
                shown.current = null;
            }

            return recorded;
        });

        return shown.current;
    }, []);

    const handleCardOnScreen = useCallback((): void => {
        stopWaiting.current?.();
        stopWaiting.current = whenTabVisible(markShown);
    }, [markShown]);

    useEffect(() => () => stopWaiting.current?.(), []);

    return {handleCardOnScreen, markShown};
};

const useAnswer = (markShown: () => Promise<boolean>): Answering => {
    const [submitting, setSubmitting] = useState(false);
    const [failed, setFailed] = useState(false);

    const answer = useCallback(async(params: AnswerParams): Promise<void> => {
        setSubmitting(true);
        setFailed(false);

        try {
            const response = await markShown() && await new Patch({params: {...params}, url: CONFIRMATION_URL}).execute();

            if (!response || !response.ok) {
                throw new Error(`country confirmation is not saved: ${params.result}`);
            }
            userStore.update({user: (await response.json()).user});
        } catch {
            setFailed(true);
            setSubmitting(false);
        }
    }, [markShown]);

    return {answer, failed, setSubmitting, submitting};
};

const CountryConfirmationCard = inject('modalsStore')(observer(({modalsStore}: CardProps): React.ReactElement => {
    const {country, country_code: userCountryCode, verified_status: verifiedStatus} = userStore.user;
    const currentCountryCode = userCountryCode === UNKNOWN_COUNTRY ? '' : userCountryCode;
    const confirmedByDocuments = verifiedStatus === 'verified' && Boolean(currentCountryCode);
    const [countryCode, setCountryCode] = useState(currentCountryCode);
    const card = useRef<HTMLDivElement>(null);
    const hidden = modalsStore.collection.length > 0 && !isDesktop();
    const {handleCardOnScreen, markShown} = useShownReport();
    const {answer, failed, setSubmitting, submitting} = useAnswer(markShown);
    const action = noticeAction(currentCountryCode, countryCode);
    const reason = currentCountryCode ? 'restricted' : 'unknown';

    const chooseAnotherCountry = useCallback((): void => {
        setCountryCode(currentCountryCode);
        setSubmitting(false);
        requestAnimationFrame(() => openCountryList(card.current));
    }, [currentCountryCode, setSubmitting]);

    const handleSubmit = useCallback(async(): Promise<void> => {
        if (action === 'confirm') {
            await answer({result: 'confirmed'});
            return;
        }

        setSubmitting(true);
        const flag = configStore.REG_FLAGS.find(item => item.code === countryCode);
        const confirmed = await confirmCountryChoice(
            countryCode,
            flag?.restrictions || [],
            restrictions => askAboutCountryRestrictions(countryCode, restrictions)
        );

        if (confirmed) {
            await answer({country_code: countryCode, result: 'changed'});
        } else {
            chooseAnotherCountry();
        }
    }, [
        action,
        answer,
        chooseAnotherCountry,
        countryCode,
        setSubmitting
    ]);

    const handleClose = useCallback(async(): Promise<void> => {
        await answer({result: 'closed'});
    }, [answer]);

    const handleCountryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>): void => {
        setCountryCode(e.target.value);
    }, []);

    return <div ref={card} className={`country-confirmation ${hidden ? 'country-confirmation--hidden' : ''}`}>
        {!hidden && <IntersectionElement callback={handleCardOnScreen}/>}
        <div className="country-confirmation__head">
            <span className="country-confirmation__title">{I18n.t(`country_confirmation.${reason}_title`)}</span>
            <button type="button" className="country-confirmation__close" disabled={submitting} onClick={handleClose}>
                <SpriteIcon name="#svg-close" size={{height: 12, width: 12}}/>
            </button>
        </div>
        <p className="country-confirmation__text">
            {I18n.t(`country_confirmation.${reason}_text`, {country})}
        </p>
        {confirmedByDocuments
            ? <p className="country-confirmation__text">{I18n.t('country_confirmation.verified_text')}</p>
            : <div className="classical-select select-with-images country-confirmation__select">
                <SelectPicker
                    value={countryCode}
                    onChange={handleCountryChange}
                    collection={countryFlags()}
                    className="select-country"
                    placeholder={I18n.t('select_a_country')}
                    disabled={submitting}
                />
                <div className="field__select-arrow"/>
            </div>}
        {failed && <p className="country-confirmation__error">{I18n.t('country_confirmation.failed')}</p>}
        <button
            type="button"
            className="button-adaptive country-confirmation__submit"
            disabled={action === 'choose' || submitting}
            onClick={handleSubmit}
        >
            {I18n.t(action === 'confirm' ? 'country_confirmation.confirm' : 'country_confirmation.save')}
        </button>
    </div>;
}));

const CountryConfirmationNotice = observer((): React.ReactElement | null => {
    const {country_confirmation_due: due, user_id: userId} = userStore.user;

    return userId && due ? <CountryConfirmationCard/> : null;
});

export default CountryConfirmationNotice;
